import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import {
  ChangeDetectionStrategy,
  Component,
  AfterContentInit,
  ChangeDetectorRef,
  Injector,
  forwardRef,
  input,
  computed
} from '@angular/core';
import {
  NG_VALUE_ACCESSOR,
  NgControl,
  ControlValueAccessor,
  Validator,
  AbstractControl,
  ValidationErrors,
  FormControl,
  ReactiveFormsModule,
  NG_VALIDATORS
} from '@angular/forms';
import { BaseControlValueAccessor } from '../base-control-value-accessor';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';

@Component({
  selector: 'lib-form-money',
  templateUrl: './form-money.component.html',
  styleUrls: ['./form-money.component.scss', '../form.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    NgxMaskDirective
  ],
  providers: [
    provideNgxMask(),
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormMoneyComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => FormMoneyComponent),
      multi: true
    }
  ]
})
export class FormMoneyComponent extends BaseControlValueAccessor<number | string> implements AfterContentInit, ControlValueAccessor, Validator {
  label = input<string>('Amount');
  startHint = input<string>();
  endHint = input<string>();
  prefix = input<string>();
  additionalClass = input<string>();
  step = input<string>();
  min = input<string>();
  max = input<string>();
  autocomplete = input<string>('off');
  minError = input<string>('Amount cannot be negative');
  maxError = input<string>('Amount exceeds maximum');
  pattern = input<string | RegExp>('');
  patternErrorMessage = input<string>();
  readonly = input<boolean>(false);
  alignment = input<'left' | 'center' | 'right'>('left');
  placeholder = input<string>();
  allowDecimals = input<boolean>(false);
  control!: FormControl;

  // Computed signals for money-specific formatting
  prefixComputed = computed<string>(() => this.prefix() ?? '$');
  maskComputed = computed<string>(() => this.allowDecimals() ? 'separator.2' : 'separator.0');
  stepComputed = computed<string>(() => this.step() ?? '0.01');
  minComputed = computed<string>(() => this.min() ?? '0');
  patternComputed = computed<string | RegExp>(() => {
    const basePattern = this.pattern();
    if (basePattern) {
      return basePattern;
    }
    return this.allowDecimals() ? /^\d*\.?\d{0,2}$/ : /^\d+$/;
  });
  patternErrorMessageComputed = computed<string>(() => {
    return this.patternErrorMessage() ?? 'Must be a valid amount with up to two decimal places';
  });
  placeholderComputed = computed<string>(() => {
    return this.placeholder() ?? (this.allowDecimals() ? '$0.00' : '$0');
  });

  constructor(private injector: Injector, private cdRef: ChangeDetectorRef) {
    super();
  }

  ngAfterContentInit(): void {
    const ngControl: NgControl = this.injector.get(NgControl, null) as NgControl;
    if (ngControl) {
      this.control = ngControl.control as FormControl;
    } else {
      this.control = new FormControl();
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return control.valid ? null : control.errors;
  }

  override writeValue(value: number | string | null): void {
    if (value === null || value === undefined) {
      this.value = '';
    } else {
      // Ensure the value is stored as a clean number string (e.g., "1234.56")
      this.value = typeof value === 'number' ? value.toFixed(2) : value.replace(/[^0-9.]/g, '');
    }
    this.cdRef.markForCheck();
  }

  get invalid(): boolean | null {
    return this.control ? this.control.invalid : false;
  }

  get showError(): boolean | null {
    if (!this.control) {
      return false;
    }
    const { dirty, touched } = this.control;
    return this.invalid ? dirty || touched : false;
  }

  floatLabel() {
    if (!this.value) {
      return false;
    } else if (!isNaN(Number(this.value))) {
      return true;
    } else if (typeof this.value === 'string') {
      return false;
    } else  {
      return true;
    }
  }
}